import { useState } from "react";
import "./App.css";

const dims = [
  {
    id: "commercial", title: "Commercial Clarity",
    q: "Does your delivery team know exactly what was sold before a project starts?",
    levels: [
      "Delivery finds out what was sold when the customer corrects them",
      "A handover call happens but notes are inconsistent and not stored centrally",
      "A handover document exists but is not always completed or shared",
      "A standard handover document is completed for all deals and stored centrally",
      "Delivery can access the full commercial record before the first customer call"
    ],
    fix: "Create and mandate the Commercial Handover Document. No customer contact until signed off by the PS Practice Lead.",
    actions: [
      "Create the Commercial Handover Document template this week",
      "Mandate it for all deals from next Monday",
      "PS Practice Lead sign-off required before any customer contact"
    ]
  },
  {
    id: "process", title: "Process Definition",
    q: "Does a documented, agreed Sales-to-Delivery handover process exist?",
    levels: [
      "No documented process — each deal handled differently depending on who is involved",
      "An informal process exists in people's heads but is not written down",
      "A process document exists but was written once and is not actively followed",
      "A documented process exists, is trained to all roles, and followed for most deals",
      "A documented process exists, followed for all deals, reviewed quarterly, and measured"
    ],
    fix: "Adopt the 5-stage framework from the playbook as your documented process. Run it on the next 2 deals as a pilot.",
    actions: [
      "Adopt the 5-stage framework as your documented process",
      "Run it on the next 2 deals as a pilot",
      "Assign a process champion for each pilot deal"
    ]
  },
  {
    id: "ownership", title: "Role Ownership",
    q: "Does everyone involved in the handover know what they own and when?",
    levels: [
      "Ownership is assumed rather than assigned — tasks fall through gaps between roles",
      "Ownership is discussed on a deal-by-deal basis in kickoff calls",
      "A RACI exists but was not agreed with all stakeholders",
      "A RACI is agreed and used for all deals — role owners know their responsibilities",
      "Role ownership is embedded in job descriptions and performance management"
    ],
    fix: "Run the RACI workshop from Part 6 of the playbook. Book 3 hours. Work through every Accountable. Do not leave until every A is agreed.",
    actions: [
      "Book a 3-hour RACI workshop with all 9 role owners",
      "Work through every Accountable — do not defer",
      "Publish the agreed RACI within 24 hours of the workshop"
    ]
  },
  {
    id: "systems", title: "System Connectivity",
    q: "Does information flow automatically between your CRM, PSA, and project tool?",
    levels: [
      "Each system is a silo — information is re-entered manually at each stage",
      "Some information is shared via email or Slack, not system integration",
      "Partial integration exists but key fields are still manual",
      "Core project data flows automatically between systems",
      "End-to-end integration with a single source of truth — no manual re-entry"
    ],
    fix: "Start with the CSV task list as your interim bridge. Map which fields are manually re-entered and build the integration business case from there.",
    actions: [
      "Audit which fields are manually re-entered across systems",
      "Use the CSV task list as an interim bridge",
      "Build the integration business case from the audit findings"
    ]
  },
  {
    id: "validation", title: "Customer Expectation Validation",
    q: "Is the customer's expectation formally validated before delivery begins?",
    levels: [
      "Delivery begins based on what Sales told PS — expectations discovered during delivery",
      "A kickoff call happens but is often the first PS-customer conversation",
      "A pre-kickoff session happens for some deals but not consistently",
      "A formal scope validation session happens for all deals before delivery begins",
      "A signed scope confirmation document is required before any PS resource is allocated"
    ],
    fix: "Implement the Customer Scope Confirmation Document. Make customer signature a hard gate before delivery starts.",
    actions: [
      "Create the Scope Confirmation Document template this week",
      "Make customer signature a hard gate before delivery",
      "Track how many deals start without a signed scope document"
    ]
  },
  {
    id: "governance", title: "Governance",
    q: "Is there a mechanism to catch and fix handover failures before they escalate?",
    levels: [
      "Failures are discovered when the customer complains or a deadline is missed",
      "Issues are escalated informally when someone notices something wrong",
      "A weekly PS ops review exists but handover health is not a standing agenda item",
      "Handover health is reviewed weekly — issues flagged and assigned owners",
      "A handover quality metric is tracked, reported monthly, and drives improvement"
    ],
    fix: "Implement the Handover Quality Scorecard. Add handover health to your weekly PS ops review. Governance without measurement is decoration.",
    actions: [
      "Implement the Handover Quality Scorecard for all deals",
      "Add handover health as a standing item on weekly PS ops review",
      "Report average score to PS leadership monthly"
    ]
  }
];

const maturityLevels = [
  { min: 6, max: 12, label: "Reactive", color: "#E24B4A", bg: "#FCEBEB", desc: "High urgency. Start with the RACI workshop and Stage 1 of the framework today." },
  { min: 13, max: 18, label: "Developing", color: "#BA7517", bg: "#FAEEDA", desc: "Foundation exists but is inconsistent. Work through Parts 2–4 of the playbook in full." },
  { min: 19, max: 24, label: "Defined", color: "#185FA5", bg: "#E6F1FB", desc: "Process exists but gaps remain. Focus on systemising and measuring your current approach." },
  { min: 25, max: 30, label: "Optimised", color: "#1D9E75", bg: "#E1F5EE", desc: "Strong baseline. Use the playbook to pressure-test and prepare for scale." }
];

function getMaturity(t) {
  return maturityLevels.find(m => t >= m.min && t <= m.max) || maturityLevels[0];
}

function scoreColor(s) {
  return s <= 2 ? "#E24B4A" : s <= 3 ? "#BA7517" : "#1D9E75";
}

function scoreBg(s) {
  return s <= 2 ? "#FCEBEB" : s <= 3 ? "#FAEEDA" : "#E1F5EE";
}

export default function App() {
  const [scores, setScores] = useState({ commercial: 3, process: 3, ownership: 3, systems: 3, validation: 3, governance: 3 });
  const [activeTab, setActiveTab] = useState("diagnostic");
  const [expanded, setExpanded] = useState(null);

  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  const maturity = getMaturity(total);
  const pct = Math.round(((total - 6) / 24) * 100);
  const sorted = [...dims].sort((a, b) => scores[a.id] - scores[b.id]);
  const topGaps = sorted.slice(0, 3);

  const phases = [
    { label: "Days 1–30", title: "Diagnose and design", color: "#042C53", dims: sorted.slice(0, 2) },
    { label: "Days 31–60", title: "Build and pilot", color: "#185FA5", dims: sorted.slice(2, 4) },
    { label: "Days 61–90", title: "Embed and measure", color: "#1D9E75", dims: sorted.slice(4, 6) }
  ];

  return (
    <div className="app">
      <header className="header">
        <div className="header-inner">
          <div className="brand">
            <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
              <path d="M18,2 C19,2.5 27,8 28,18 C29,26 24,32 18,34 C12,32 7,26 8,18 C9,8 17,2.5 18,2 Z" fill="#FFFFFF"/>
              <line x1="18" y1="4" x2="18" y2="31" stroke="#1D9E75" strokeWidth="2" strokeLinecap="round"/>
              <path d="M18,13 C21,14 25,16 27,20" fill="none" stroke="#1D9E75" strokeWidth="1.3" strokeLinecap="round"/>
              <path d="M18,13 C15,14 11,16 9,20" fill="none" stroke="#1D9E75" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            <div>
              <div className="brand-name">Bodhi Professional</div>
              <div className="brand-tag">SaaS PS Project Launch Diagnostic</div>
            </div>
          </div>
          <a href="https://bodhiprofessional.com" target="_blank" rel="noreferrer" className="header-link">bodhiprofessional.com →</a>
        </div>
      </header>

      <main className="main">
        <div className="container">
          <div className="intro">
            <h1>Sales-to-Delivery Handover Diagnostic</h1>
            <p>Score your organisation across 6 dimensions. Get your maturity level and a personalised 90-day implementation roadmap.</p>
          </div>

          <div className="tabs">
            {[["diagnostic", "Diagnostic"], ["results", "Results"], ["roadmap", "90-Day Roadmap"]].map(([id, label]) => (
              <button key={id} className={`tab ${activeTab === id ? "active" : ""}`} onClick={() => setActiveTab(id)}>{label}</button>
            ))}
          </div>

          {activeTab === "diagnostic" && (
            <div>
              <div className="summary-bar">
                <div className="summary-bar-left">
                  <div className="summary-label">Overall maturity score</div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${pct}%`, background: maturity.color }} />
                  </div>
                </div>
                <div className="summary-score" style={{ color: maturity.color }}>{total}<span>/30</span></div>
                <div className="maturity-pill" style={{ background: maturity.bg, color: maturity.color }}>{maturity.label}</div>
              </div>

              {dims.map(d => {
                const s = scores[d.id];
                const isExp = expanded === d.id;
                return (
                  <div key={d.id} className="dim-card">
                    <div className="dim-header" onClick={() => setExpanded(isExp ? null : d.id)}>
                      <div className="dim-icon" style={{ background: scoreBg(s) }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={scoreColor(s)} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          {d.id === "commercial" && <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/></>}
                          {d.id === "process" && <><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 19.07a10 10 0 0 1 0-14.14"/></>}
                          {d.id === "ownership" && <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>}
                          {d.id === "systems" && <><circle cx="12" cy="5" r="3"/><circle cx="19" cy="19" r="3"/><circle cx="5" cy="19" r="3"/><line x1="12" y1="8" x2="12" y2="14"/><line x1="12" y1="14" x2="19" y2="16"/><line x1="12" y1="14" x2="5" y2="16"/></>}
                          {d.id === "validation" && <><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>}
                          {d.id === "governance" && <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>}
                        </svg>
                      </div>
                      <div className="dim-meta">
                        <div className="dim-title">{d.title}</div>
                        <div className="dim-q">{d.q}</div>
                      </div>
                      <div className="dim-right">
                        <div className="dim-score" style={{ color: scoreColor(s) }}>{s}</div>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round">
                          {isExp ? <polyline points="18,15 12,9 6,15"/> : <polyline points="6,9 12,15 18,9"/>}
                        </svg>
                      </div>
                    </div>

                    {isExp && (
                      <div className="dim-body">
                        <div className="slider-wrap">
                          <input type="range" min={1} max={5} step={1} value={s}
                            onChange={e => setScores(sc => ({ ...sc, [d.id]: parseInt(e.target.value) }))} />
                          <div className="slider-labels">
                            {[1,2,3,4,5].map(n => (
                              <span key={n} style={{ fontWeight: n === s ? 600 : 400, color: n === s ? "#185FA5" : "#888" }}>{n}</span>
                            ))}
                          </div>
                        </div>
                        <div className="level-box" style={{ borderLeftColor: scoreColor(s) }}>
                          <div className="level-label">Score {s}</div>
                          <div className="level-text">{d.levels[s - 1]}</div>
                        </div>
                        {s <= 3 && (
                          <div className="fix-box">
                            <div className="fix-label">Recommended action</div>
                            <div className="fix-text">{d.fix}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              <button className="cta-btn navy" onClick={() => setActiveTab("results")}>
                See my results →
              </button>
            </div>
          )}

          {activeTab === "results" && (
            <div>
              <div className="maturity-hero" style={{ background: maturity.bg, border: `1px solid ${maturity.color}` }}>
                <div className="maturity-score" style={{ color: maturity.color }}>{total}<span>/30</span></div>
                <div>
                  <div className="maturity-label" style={{ color: maturity.color }}>{maturity.label}</div>
                  <div className="maturity-desc" style={{ color: maturity.color }}>{maturity.desc}</div>
                </div>
              </div>

              <div className="score-grid">
                {dims.map(d => (
                  <div key={d.id} className="score-card">
                    <div className="score-card-label">{d.title}</div>
                    <div className="score-card-num" style={{ color: scoreColor(scores[d.id]) }}>{scores[d.id]}</div>
                    <div className="mini-bar">
                      <div className="mini-fill" style={{ width: `${(scores[d.id]/5)*100}%`, background: scoreColor(scores[d.id]) }} />
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="section-title">Your 3 highest-priority gaps</h2>
              {topGaps.map((d, i) => (
                <div key={d.id} className="gap-card">
                  <div className="gap-num" style={{ background: ["#FCEBEB","#FAEEDA","#E6F1FB"][i], color: ["#E24B4A","#BA7517","#185FA5"][i] }}>{i+1}</div>
                  <div>
                    <div className="gap-title">{d.title} — score: {scores[d.id]}/5</div>
                    <div className="gap-fix">{d.fix}</div>
                  </div>
                </div>
              ))}

              <div className="playbook-cta">
                <div className="playbook-cta-title">Want the full playbook?</div>
                <div className="playbook-cta-body">The SaaS PS Project Launch Playbook includes the complete 5-stage framework, full RACI for all 9 roles, 7 document templates, and a CSV task list that imports into MS Project, Jira, Asana, and Monday.com. £49.99 instant download.</div>
                <a href="https://bodhiprofessional.lemonsqueezy.com" target="_blank" rel="noreferrer" className="cta-btn teal">Get the full playbook →</a>
              </div>

              <button className="cta-btn navy" onClick={() => setActiveTab("roadmap")}>
                See my 90-day roadmap →
              </button>
            </div>
          )}

          {activeTab === "roadmap" && (
            <div>
              <p className="intro-text">Based on your scores, here is your personalised 90-day implementation roadmap — prioritised by your lowest-scoring dimensions.</p>

              {phases.map((phase, pi) => (
                <div key={pi} className="phase">
                  <div className="phase-header">
                    <span className="phase-badge" style={{ background: phase.color }}>{phase.label}</span>
                    <span className="phase-title">{phase.title}</span>
                  </div>
                  {phase.dims.map(d => (
                    <div key={d.id} className="phase-dim">
                      <div className="phase-dim-header">
                        <span className="phase-dim-title">{d.title}</span>
                        <span className="phase-dim-score" style={{ color: phase.color }}>Current: {scores[d.id]}/5</span>
                      </div>
                      {d.actions.map((action, ai) => (
                        <div key={ai} className="action-item">
                          <div className="action-num" style={{ borderColor: phase.color, color: phase.color }}>{ai + 1}</div>
                          <div className="action-text">{action}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}

              <div className="consulting-cta">
                <div className="consulting-title">Need hands-on help implementing this?</div>
                <div className="consulting-body">Bodhi Professional offers time-based implementation consultancy — from a 2-day diagnostic sprint to a 20-day transformation programme. Knowledge transfer built into every engagement.</div>
                <div className="consulting-tiers">
                  {[
                    { tier: "Diagnostic Sprint", days: "2 days", desc: "RACI workshop + gap analysis + prioritised action plan" },
                    { tier: "Implementation Sprint", days: "10 days", desc: "Full framework implementation with pilot deal support" },
                    { tier: "Transformation Programme", days: "20 days", desc: "90-day guided implementation with weekly advisory sessions" }
                  ].map((t, i) => (
                    <div key={i} className="tier-card">
                      <div className="tier-name">{t.tier}</div>
                      <div className="tier-days">{t.days}</div>
                      <div className="tier-desc">{t.desc}</div>
                    </div>
                  ))}
                </div>
                <a href="mailto:hello@bodhiprofessional.com?subject=PS Launch Playbook — Consulting Enquiry" className="cta-btn teal">
                  Get in touch →
                </a>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <span>© Bodhi Professional</span>
          <span>·</span>
          <a href="https://bodhiprofessional.com" target="_blank" rel="noreferrer">bodhiprofessional.com</a>
          <span>·</span>
          <span>AI tools for the awakened professional</span>
        </div>
      </footer>
    </div>
  );
}
